import {
  DealListSort,
  DealListTab,
  DealSalesScope,
  type GetDealsListQuery,
} from '@/lib/graphql/generated/graphql';
import { atom } from 'jotai';

export const DEALS_PAGE_SIZE = 50;
export const DEAL_TABS = [
  DealListTab.NotStarted,
  DealListTab.InProgress,
  DealListTab.Closed,
] as const;
export const TAB_COUNT_KEYS = {
  [DealListTab.NotStarted]: 'notStarted',
  [DealListTab.InProgress]: 'inProgress',
  [DealListTab.Closed]: 'closed',
} as const;

export type SalesSelection = {
  salesScope: DealSalesScope;
  managerIds: string[];
};
export type ManagerOption = GetDealsListQuery['listDeals']['managers'][number];
export function selectedManagerIds(
  selection: SalesSelection,
  managers: ManagerOption[]
) {
  if (selection.salesScope === DealSalesScope.All)
    return managers.map((manager) => manager.id);
  if (selection.salesScope === DealSalesScope.Me)
    return managers
      .filter((manager) => manager.isMe)
      .map((manager) => manager.id);
  return selection.managerIds;
}

export const DEFAULT_DEALS_LIST_STATE = {
  tab: DealListTab.NotStarted,
  sorts: {
    [DealListTab.NotStarted]: DealListSort.Asc,
    [DealListTab.InProgress]: DealListSort.Asc,
    [DealListTab.Closed]: DealListSort.Desc,
  },
  page: 1,
  sales: { salesScope: DealSalesScope.Me, managerIds: [] } as SalesSelection,
  searchText: '',
  search: '',
};
export type DealsListState = typeof DEFAULT_DEALS_LIST_STATE;
export const dealsListState = atom(DEFAULT_DEALS_LIST_STATE);

export const dealsListInput = atom((get) => {
  const state = get(dealsListState);
  return {
    tab: state.tab,
    sort: state.sorts[state.tab],
    ...state.sales,
    page: state.page,
    pageSize: DEALS_PAGE_SIZE,
    ...(state.search ? { search: state.search } : {}),
  };
});

export const setDealsTab = atom(null, (get, set, tab: DealListTab) => {
  set(dealsListState, { ...get(dealsListState), tab, page: 1 });
});
export const toggleDealsSort = atom(null, (get, set) => {
  const state = get(dealsListState);
  set(dealsListState, {
    ...state,
    page: 1,
    sorts: {
      ...state.sorts,
      [state.tab]:
        state.sorts[state.tab] === DealListSort.Asc
          ? DealListSort.Desc
          : DealListSort.Asc,
    },
  });
});
export const setDealsPage = atom(null, (get, set, page: number) => {
  set(dealsListState, { ...get(dealsListState), page: Math.max(1, page) });
});

export const setDealsSales = atom(null, (get, set, sales: SalesSelection) => {
  if (
    sales.salesScope === DealSalesScope.Selected &&
    (sales.managerIds.length === 0 || sales.managerIds.some((id) => !id.trim()))
  )
    return;
  set(dealsListState, {
    ...get(dealsListState),
    sales: {
      salesScope: sales.salesScope,
      managerIds:
        sales.salesScope === DealSalesScope.Selected
          ? [...new Set(sales.managerIds)]
          : [],
    },
    page: 1,
  });
});
export const setDealsSearchText = atom(null, (get, set, searchText: string) => {
  set(dealsListState, { ...get(dealsListState), searchText });
});
export const setDealsSearch = atom(null, (get, set, search: string) => {
  set(dealsListState, {
    ...get(dealsListState),
    search: search.trim(),
    page: 1,
  });
});
